import {
  createContext, useContext, useMemo,
} from 'react';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useRecordRefsQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { useRecordRefsVCQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { filterRefsByProfitCentre, recordRefsToUnitRefs } from 'utils/unitRefs';

import type IUnitRef from 'utils/interfaces/IUnitRef';
import type IUnitRefsProvider from './interfaces/IUnitRefsProvider';

interface IUnitRefsContextValue {
  getRefs: (type: string, profitCentreUID?: string) => IUnitRef[],
  getRef: (type: string, uid: string) => IUnitRef | undefined,
  isLoading: boolean,
}

const EMPTY_REFS: IUnitRef[] = [];

const UnitRefsContext = createContext<IUnitRefsContextValue>({
  getRefs: () => EMPTY_REFS,
  getRef: () => undefined,
  isLoading: false,
});

// Single place the unit pickers get their data from. Components ask this for refs rather
// than reading a query result, so re-pointing at another source is a change here only.
function UnitRefsProvider({ valueChainMapId, children }: IUnitRefsProvider) {
  const isVCScoped = Boolean(valueChainMapId);

  const supplierVC = useRecordRefsVCQuery(
    { type: VALUE_CHAIN_UNIT_TYPES.SUPPLIER, valueChainMapId: valueChainMapId as string },
    { skip: !isVCScoped },
  );
  const assetVC = useRecordRefsVCQuery(
    { type: VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET, valueChainMapId: valueChainMapId as string },
    { skip: !isVCScoped },
  );
  const supplierGlobal = useRecordRefsQuery(
    { type: VALUE_CHAIN_UNIT_TYPES.SUPPLIER },
    { skip: isVCScoped },
  );
  const assetGlobal = useRecordRefsQuery(
    { type: VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET },
    { skip: isVCScoped },
  );

  const supplier = isVCScoped ? supplierVC : supplierGlobal;
  const asset = isVCScoped ? assetVC : assetGlobal;

  const refsByType = useMemo(() => ({
    [VALUE_CHAIN_UNIT_TYPES.SUPPLIER]: recordRefsToUnitRefs(
      supplier.data,
      VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
    ),
    [VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET]: recordRefsToUnitRefs(
      asset.data,
      VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
    ),
  }), [asset.data, supplier.data]);

  const value = useMemo<IUnitRefsContextValue>(() => ({
    getRefs: (type, profitCentreUID) => filterRefsByProfitCentre(
      refsByType[type] || EMPTY_REFS,
      profitCentreUID,
    ),
    getRef: (type, uid) => (refsByType[type] || EMPTY_REFS).find((ref) => ref.uid === uid),
    isLoading: supplier.isLoading || asset.isLoading,
  }), [asset.isLoading, refsByType, supplier.isLoading]);

  return (
    <UnitRefsContext.Provider value={value}>
      {children}
    </UnitRefsContext.Provider>
  );
}

export const useUnitRefs = () => useContext(UnitRefsContext);

export default UnitRefsProvider;
